<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    title: $fn.value.pgettext('Job|ModalHeader', 'Job'),
  });
  const ux = ref([]);
  const b_1382594685840889_style = computed(() => ({
    maxWidth: '80px',
  }));

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
    b_1382594685840889_style: b_1382594685840889_style.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_1382594685840889_style', nodeId: '1382594685840889' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux, b_1382594685840889_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return $fn.value.pgettext('Job|ModalHeader', 'Job');
  });
  const resourceKey = computed(() => {
    return '#' + props.item.number;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_2754826507016131_emit_5vjhhm9i8_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7463771596263925_emit_ioi1nzn4d_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7463771596263925_request_1ik75hy6p_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.item.links.retry,
      method: 'PATCH',
      headers: {
        'If-Match': props.item.etag,
      },
    };
  };
  const act_7763705103899431_emit_bwuv8nfmc_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7763705103899431_request_j1qkh6ih7_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.item.links.cancel,
      method: 'PATCH',
      headers: {
        'If-Match': props.item.etag,
      },
    };
  };

  const act_2754826507016131_emit_5vjhhm9i8 = new actionTypes.EmitAction({
    actionArgs: act_2754826507016131_emit_5vjhhm9i8_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_2754826507016131_clickPreventStop_vgswmqrwe =
    new actionTypes.EventHandlerImpl({
      action: act_2754826507016131_emit_5vjhhm9i8,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_2754826507016131_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2754826507016131_clickPreventStop_vgswmqrwe],
      event: 'click.prevent.stop',
    });
  const act_7463771596263925_emit_ioi1nzn4d = new actionTypes.EmitAction({
    actionArgs: act_7463771596263925_emit_ioi1nzn4d_getActionArgs,
  });
  const inst_evh_7463771596263925_success_ssxxxcuvp =
    new actionTypes.EventHandlerImpl({
      action: act_7463771596263925_emit_ioi1nzn4d,
      event: 'success',
      displayName: 'emit',
    });
  const act_7463771596263925_request_1ik75hy6p = new actionTypes.RequestAction({
    actionArgs: act_7463771596263925_request_1ik75hy6p_getActionArgs,
    displayName: 'retryJob',
    events: [inst_evh_7463771596263925_success_ssxxxcuvp],
  });
  const inst_evh_7463771596263925_clickPreventStop_8bsnqcz0w =
    new actionTypes.EventHandlerImpl({
      action: act_7463771596263925_request_1ik75hy6p,
      event: 'click.prevent.stop',
      displayName: 'retryJob',
    });
  const inst_evh_7463771596263925_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7463771596263925_clickPreventStop_8bsnqcz0w],
      event: 'click.prevent.stop',
    });
  const act_7763705103899431_emit_bwuv8nfmc = new actionTypes.EmitAction({
    actionArgs: act_7763705103899431_emit_bwuv8nfmc_getActionArgs,
  });
  const inst_evh_7763705103899431_success_0t76wazdi =
    new actionTypes.EventHandlerImpl({
      action: act_7763705103899431_emit_bwuv8nfmc,
      event: 'success',
      displayName: 'emit',
    });
  const act_7763705103899431_request_j1qkh6ih7 = new actionTypes.RequestAction({
    actionArgs: act_7763705103899431_request_j1qkh6ih7_getActionArgs,
    displayName: 'cancelJob',
    events: [inst_evh_7763705103899431_success_0t76wazdi],
  });
  const inst_evh_7763705103899431_clickPreventStop_kf0uz3l6j =
    new actionTypes.EventHandlerImpl({
      action: act_7763705103899431_request_j1qkh6ih7,
      event: 'click.prevent.stop',
      displayName: 'cancelJob',
    });
  const inst_evh_7763705103899431_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7763705103899431_clickPreventStop_kf0uz3l6j],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_2754826507016131_clickPreventStop_vgswmqrwe,
    reload: inst_evh_2754826507016131_clickPreventStop_vgswmqrwe,
    inst_evh_2754826507016131_clickPreventStop,
    inst_evh_7463771596263925_success_ssxxxcuvp,
    inst_evh_7463771596263925_clickPreventStop_8bsnqcz0w,
    retryJob: inst_evh_7463771596263925_clickPreventStop_8bsnqcz0w,
    inst_evh_7463771596263925_clickPreventStop,
    inst_evh_7763705103899431_success_0t76wazdi,
    inst_evh_7763705103899431_clickPreventStop_kf0uz3l6j,
    cancelJob: inst_evh_7763705103899431_clickPreventStop_kf0uz3l6j,
    inst_evh_7763705103899431_clickPreventStop,
  };

  const evh_2754826507016131_clickPreventStop_vgswmqrwe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2754826507016131_clickPreventStop_vgswmqrwe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2754826507016131_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2754826507016131_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7463771596263925_success_ssxxxcuvp = async (event, scope) => {
    return await actions.inst_evh_7463771596263925_success_ssxxxcuvp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7463771596263925_clickPreventStop_8bsnqcz0w = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7463771596263925_clickPreventStop_8bsnqcz0w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7463771596263925_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7463771596263925_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7763705103899431_success_0t76wazdi = async (event, scope) => {
    return await actions.inst_evh_7763705103899431_success_0t76wazdi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7763705103899431_clickPreventStop_kf0uz3l6j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7763705103899431_clickPreventStop_kf0uz3l6j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7763705103899431_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7763705103899431_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" :gap="2" class="w-100" v-if="loaded">
    <ItpText muted :text="resourceTitle" class="mb-0"> </ItpText>
    <ItpText tag="h5" :text="resourceKey" class="mb-0"> </ItpText>
    <UcJobNameBadge :value="item.name"> </UcJobNameBadge>
    <UcJobStatusBadge :value="item.status"> </UcJobStatusBadge>
    <ItpText strong>
      {{ datetime(item.submit_time) }}
    </ItpText>
    <ItpText
      strong
      no-wrap
      class="text-truncate"
      :style="b_1382594685840889_style"
    >
      {{ username(item.created_by) }}
    </ItpText>
    <BButtonGroup class="ms-auto">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        class="ms-auto"
        @click.prevent.stop="evh_2754826507016131_clickPreventStop($event, {})"
      >
      </ItpButton>
    </BButtonGroup>
    <BDropdown right variant="light" class="border">
      <template #button-content>
        <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
      </template>
      <ItpButton
        dropdown-item
        :text="pgettext('Job', 'Retry')"
        size="sm"
        class="me-2"
        v-if="item.links.retry"
        @click.prevent.stop="evh_7463771596263925_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        dropdown-item
        :text="pgettext('Job', 'Cancel')"
        size="sm"
        class="me-2"
        v-if="item.links.cancel"
        @click.prevent.stop="evh_7763705103899431_clickPreventStop($event, {})"
      >
      </ItpButton>
    </BDropdown>
  </ItpBox>
</template>
